/**
 * Pure (JSX-free, DOM-free) auth logic shared by the front-door components: the
 * login/sign-up submit gates, and the {@link authGateView} decision `AuthGate`
 * renders off. Kept out of the components so both are unit-tested with no DOM.
 * The edge is the authority on every credential; the submit gates only avoid an
 * obviously-invalid round-trip and keep the client-side rules (min password
 * length, the 18+ attestation) in one tested place.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The edge's minimum password length (`registerSchema` — `z.string().min(8)`). */
export const PASSWORD_MIN_LENGTH = 8;

/** The display-name bounds the edge enforces (`z.string().min(1).max(80)`). */
export const DISPLAY_NAME_MAX_LENGTH = 80;

/** Whether an email looks well-formed enough to bother sending. */
export function isEmailValid(email: string): boolean {
  return EMAIL_RE.test(email.trim());
}

/** A login is submittable once the email looks valid and a password is present. */
export function isLoginSubmittable(email: string, password: string): boolean {
  return isEmailValid(email) && password.length > 0;
}

/**
 * Whether the bot gate is satisfied for this build.
 *
 * The two halves are configured independently — the site key is baked into the
 * bundle, `HCAPTCHA_SECRET` is sealed on the edge — so this is the one place
 * that maps "which halves exist" to "may we submit":
 *
 *   - no site key ⇒ the edge is running `NullCaptchaVerifier` and expects no
 *     token, so the gate must NOT block (that is the local + test loop).
 *   - a site key ⇒ nothing is submitted until the challenge is solved, because
 *     `HCaptchaVerifier.verify` returns `false` on an empty token and the
 *     round-trip would 403 with no explanation.
 */
export function isCaptchaSatisfied(sitekey: string | null, token: string | null): boolean {
  return sitekey === null || token !== null;
}

/** The fields the sign-up gate reads (mirrors the register request the wizard builds). */
export interface SignupDraft {
  readonly email: string;
  readonly password: string;
  readonly displayName: string;
  /** The 18+ self-attestation checkbox — compliance requires it before sign-up. */
  readonly declaredOver18: boolean;
}

/**
 * A sign-up is submittable once the email is valid, the password meets the
 * minimum length, a non-empty display name is set, AND the user has attested to
 * being 18+ (the compliance red line — no account without it).
 */
export function isSignupSubmittable(draft: SignupDraft): boolean {
  const name = draft.displayName.trim();
  return (
    isEmailValid(draft.email) &&
    draft.password.length >= PASSWORD_MIN_LENGTH &&
    name.length > 0 &&
    name.length <= DISPLAY_NAME_MAX_LENGTH &&
    draft.declaredOver18
  );
}

/** Which surface the auth boundary (`AuthGate`) shows. */
export type AuthGateView = 'restoring' | 'unreachable' | 'front-door' | 'age-gate' | 'surface';

/**
 * The four facts the gate reads. Built by calling the matching
 * `SessionStore` accessors (`restored()`, `unreachable()`, `authenticated()`,
 * `ageOk()`) — kept as plain booleans here, rather than taking the store
 * directly, so this stays trivially unit-testable with a literal object.
 */
export interface AuthGateState {
  readonly restored: boolean;
  /** The boot restore could not reach the edge (as opposed to being refused by it). */
  readonly unreachable: boolean;
  readonly authenticated: boolean;
  readonly ageOk: boolean;
}

/**
 * Decide which surface `AuthGate` renders. `restored` outranks everything: the
 * boot `restore()` round-trip leaves `authenticated`/`ageOk` at their
 * logged-out defaults for its whole duration, so an already signed-in reload
 * that gated on `authenticated` alone would flash the front door before
 * `restore()` resolves. This has to key on the boot restore having SETTLED
 * (`restored`), NOT on `loading` — `loading` is ALSO true while a
 * login/register/verifyAge submit is in flight, and swapping the in-progress
 * form for a splash mid-submit would be a worse bug than the flash this fixes.
 */
export function authGateView(state: AuthGateState): AuthGateView {
  if (!state.restored) {
    return 'restoring';
  }
  // Outranks `authenticated` deliberately. `authenticated` derives from `user()`,
  // which a boot restore that never reached the edge leaves null — so without this
  // an unreachable API falls through to the front door and asks the visitor to log
  // in against an edge that cannot answer. Keeping their token is not by itself
  // enough to keep them off that form.
  if (state.unreachable) {
    return 'unreachable';
  }
  if (!state.authenticated) {
    return 'front-door';
  }
  if (!state.ageOk) {
    return 'age-gate';
  }
  return 'surface';
}

/** Which credential form the front door is showing. */
export type AuthMode = 'login' | 'signup';

/**
 * The product paths marketing's link builders emit (`apps/marketing`
 * `app-links.ts` — `buildSignupUrl` / `buildLoginUrl`). They are a WIRE CONTRACT
 * between the two apps: the SPA is served for any unknown path
 * (`docker/nginx/frontend.conf` rewrites everything to `index.html`), so these
 * strings are the only thing that says which door the visitor asked for.
 */
const SIGNUP_PATH = '/signup';
const LOGIN_PATH = '/login';

/**
 * Which front-door mode a landing URL asks for, or `undefined` when it asks for
 * neither and the existing defaults should decide.
 *
 * This exists because every sign-up CTA on the marketing site opened the SIGN-IN
 * form. The hash router only ever parsed `#/c/<id>` and `location.pathname` was
 * read nowhere in the SPA, so `/signup` and `/login` both booted the same screen
 * — a visitor who clicked "Choose Plus" was shown a password field for an account
 * they did not have.
 *
 * Read ONCE at boot, next to `captureReferralCode`, and for the same reason: the
 * landing path is a fact about how the visitor arrived, not reactive state. It
 * must not be re-read later, or the login⇄sign-up toggle would fight the URL.
 *
 * Returns `undefined` rather than defaulting to `'login'` so a bare `/` keeps the
 * referral rule that already exists — an invited visitor still opens on sign-up.
 * Trailing slashes and case are tolerated: `/Signup/` is the same request.
 */
export function readAuthMode(pathname: string): AuthMode | undefined {
  const path = pathname.toLowerCase().replace(/\/+$/, '');
  if (path === SIGNUP_PATH) {
    return 'signup';
  }
  if (path === LOGIN_PATH) {
    return 'login';
  }
  return undefined;
}
